import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP, todayStr } from '@/lib/utils';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import Autocomplete from '@/components/Autocomplete';
import { ArrowLeft, Plus } from 'lucide-react';

export default function DealForm() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [newSector, setNewSector] = useState('');
  const [showNewSector, setShowNewSector] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: '', client_id: '', owner_name: '', owner_id: '', property_address: '', property_id: '',
    deal_type: 'venta', closing_value: '', published_value: '', paid_to_date: '', paid_date: '',
    next_payment_value: '', next_payment_date: '', next_step: '', next_step_date: '', closing_date: todayStr(),
    is_external_broker: false, external_broker_name: '', external_broker_handles: 'client',
    client_agent_id: '', client_agent_name: '', owner_agent_id: '', owner_agent_name: '',
    commission_pct: '', external_split: 50, agency_split: 20, client_broker_split: 40, owner_broker_split: 40,
    sector: '', deeplook_linked: false, deeplook_commission_pct: 20,
  });

  useEffect(() => {
    Promise.all([api.get('/crm/all-clients'), api.get('/properties'), api.get('/users/brokers'), api.get('/sectors')])
      .then(([c, p, b, s]) => { setClients(c); setProperties(p); setBrokers(b); setSectors(s); });
  }, []);

  const upd = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const commission = useMemo(() => {
    const closing = parseFloat(form.closing_value) || 0;
    const pct = parseFloat(form.commission_pct) || (form.deal_type === 'venta' ? 3 : 100);
    const total = closing * pct / 100;
    const extSplit = form.is_external_broker ? parseFloat(form.external_split) || 50 : 0;
    const selectoTotal = form.is_external_broker ? total * (100 - extSplit) / 100 : total;
    const agSplit = parseFloat(form.agency_split) || 20;
    const clSplit = parseFloat(form.client_broker_split) || 40;
    const owSplit = parseFloat(form.owner_broker_split) || 40;
    const dlPct = form.deeplook_linked ? (parseFloat(form.deeplook_commission_pct) || 20) : 0;
    return { total, selecto: selectoTotal, agency: selectoTotal * agSplit / 100, clientBroker: selectoTotal * clSplit / 100, ownerBroker: selectoTotal * owSplit / 100, deeplook: selectoTotal * dlPct / 100 };
  }, [form.closing_value, form.commission_pct, form.deal_type, form.is_external_broker, form.external_split, form.agency_split, form.client_broker_split, form.owner_broker_split, form.deeplook_linked, form.deeplook_commission_pct]);

  const save = async () => {
    if (!form.client_name || !form.owner_name || !form.property_address || !form.closing_value) {
      return toast.error('Completa los campos obligatorios');
    }
    setSaving(true);
    try {
      const payload = { ...form, closing_value: parseFloat(form.closing_value) || 0, published_value: parseFloat(form.published_value) || 0,
        paid_to_date: parseFloat(form.paid_to_date) || 0, next_payment_value: parseFloat(form.next_payment_value) || 0,
        commission_pct: parseFloat(form.commission_pct) || null, external_split: parseFloat(form.external_split),
        agency_split: parseFloat(form.agency_split), client_broker_split: parseFloat(form.client_broker_split), owner_broker_split: parseFloat(form.owner_broker_split),
        sector: form.sector || null };
      await api.post('/deals', payload);
      toast.success('Negocio creado exitosamente');
      navigate('/negocios');
    } catch { toast.error('Error al crear negocio'); }
    setSaving(false);
  };

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";
  const sectionCls = "bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4";

  return (
    <div data-testid="deal-form-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/negocios')} className="text-white/50 hover:text-white transition-colors">
          <ArrowLeft size={20} strokeWidth={1.5} />
        </button>
        <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Crear Nuevo Negocio</h1>
      </div>

      {/* Section 1: PARTES */}
      <div className={sectionCls}>
        <h2 className="font-outfit text-lg font-medium text-white border-b border-white/10 pb-3">1. Partes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Cliente *</Label>
            <Autocomplete items={clients} value={form.client_name} secondaryField="phone"
              onChange={v => { upd('client_name', v); upd('client_id', ''); }}
              onSelect={c => { upd('client_name', c.name); upd('client_id', c.client_id || c.id); }}
              placeholder="Buscar cliente..." />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Propietario *</Label>
            <Autocomplete items={clients} value={form.owner_name} secondaryField="phone"
              onChange={v => { upd('owner_name', v); upd('owner_id', ''); }}
              onSelect={c => { upd('owner_name', c.name); upd('owner_id', c.client_id || c.id); }}
              placeholder="Buscar propietario..." />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Inmueble *</Label>
            <Autocomplete items={properties} value={form.property_address} displayField="address"
              onChange={v => upd('property_address', v)} onSelect={p => { upd('property_address', p.address); upd('property_id', p.id); }}
              placeholder="Buscar propiedad..." />
          </div>
        </div>
      </div>

      {/* Section 2: NEGOCIO */}
      <div className={sectionCls}>
        <h2 className="font-outfit text-lg font-medium text-white border-b border-white/10 pb-3">2. Negocio</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo</Label>
            <Select value={form.deal_type} onValueChange={v => upd('deal_type', v)}>
              <SelectTrigger data-testid="deal-type-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                <SelectItem value="venta">Venta</SelectItem>
                <SelectItem value="arriendo">Arriendo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Sector</Label>
            {!showNewSector ? (
              <div className="flex gap-2">
                <Select value={form.sector} onValueChange={v => { if (v === '__new__') { setShowNewSector(true); } else upd('sector', v); }}>
                  <SelectTrigger data-testid="sector-select" className={`${inputCls} flex-1`}><SelectValue placeholder="Seleccionar sector" /></SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    <SelectItem value="__new__">+ Agregar nuevo sector</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={newSector} onChange={e => setNewSector(e.target.value)} placeholder="Nombre del sector" className={`${inputCls} flex-1`} data-testid="new-sector-input" />
                <button onClick={async () => {
                  if (!newSector.trim()) return;
                  await api.post('/sectors', { name: newSector.trim() });
                  const updated = await api.get('/sectors');
                  setSectors(updated);
                  upd('sector', newSector.trim());
                  setNewSector('');
                  setShowNewSector(false);
                  toast.success('Sector agregado');
                }} className="h-11 px-3 bg-[#6BCB77]/20 text-[#6BCB77] rounded-xl hover:bg-[#6BCB77]/30 transition-colors">
                  <Plus size={18} />
                </button>
                <button onClick={() => setShowNewSector(false)} className="h-11 px-3 bg-white/5 text-white/50 rounded-xl hover:bg-white/10 transition-colors text-xs">
                  Cancelar
                </button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor de Cierre *</Label>
            <Input data-testid="closing-value" type="number" value={form.closing_value} onChange={e => upd('closing_value', e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor Publicado</Label>
            <Input data-testid="published-value" type="number" value={form.published_value} onChange={e => upd('published_value', e.target.value)} placeholder="0" className={inputCls} />
          </div>
        </div>
        {form.published_value && form.closing_value && (
          <p className="text-sm text-[#A38560]">Negociacion: {(((parseFloat(form.published_value) - parseFloat(form.closing_value)) / parseFloat(form.published_value)) * 100).toFixed(1)}%</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Pagado a la fecha</Label>
            <Input type="number" value={form.paid_to_date} onChange={e => upd('paid_to_date', e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha de pago</Label>
            <Input type="date" value={form.paid_date} onChange={e => upd('paid_date', e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Sig. pago valor</Label>
            <Input type="number" value={form.next_payment_value} onChange={e => upd('next_payment_value', e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha sig. pago</Label>
            <Input type="date" value={form.next_payment_date} onChange={e => upd('next_payment_date', e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Siguiente paso</Label>
            <Input value={form.next_step} onChange={e => upd('next_step', e.target.value)} placeholder="Ej: Firma de promesa" className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha sig. paso</Label>
            <Input type="date" value={form.next_step_date} onChange={e => upd('next_step_date', e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha de cierre</Label>
            <Input type="date" value={form.closing_date} onChange={e => upd('closing_date', e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>

      {/* Section 3: AGENTES */}
      <div className={sectionCls}>
        <h2 className="font-outfit text-lg font-medium text-white border-b border-white/10 pb-3">3. Agentes</h2>
        <div className="flex items-center gap-3">
          <Switch checked={form.is_external_broker} onCheckedChange={v => upd('is_external_broker', v)} data-testid="external-broker-switch" />
          <Label className="text-sm text-white">Negocio con broker externo</Label>
        </div>
        {form.is_external_broker && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[#16302B] rounded-xl">
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre del Broker Aliado</Label>
              <Input value={form.external_broker_name} onChange={e => upd('external_broker_name', e.target.value)} placeholder="Nombre" className={inputCls} />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Maneja</Label>
              <Select value={form.external_broker_handles} onValueChange={v => upd('external_broker_handles', v)}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="owner">Propietario</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 p-3 bg-purple-500/5 rounded-xl border border-purple-500/10">
          <Switch checked={form.deeplook_linked} onCheckedChange={v => upd('deeplook_linked', v)} data-testid="deeplook-switch" />
          <div>
            <Label className="text-sm text-white">Cliente / Propietario llego por Deeplook</Label>
            <p className="text-[10px] text-white/30">Si se activa, Deeplook podra ver este negocio y aparecera en la dispersion de comisiones</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Agente SELECTO - Cliente</Label>
            <Select value={form.client_agent_id} onValueChange={v => { const b = brokers.find(x => x.id === v); upd('client_agent_id', v); upd('client_agent_name', b?.name || ''); }}>
              <SelectTrigger data-testid="client-agent-select" className={inputCls}><SelectValue placeholder="Seleccionar agente" /></SelectTrigger>
              <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Agente SELECTO - Propietario</Label>
            <Select value={form.owner_agent_id} onValueChange={v => { const b = brokers.find(x => x.id === v); upd('owner_agent_id', v); upd('owner_agent_name', b?.name || ''); }}>
              <SelectTrigger data-testid="owner-agent-select" className={inputCls}><SelectValue placeholder="Seleccionar agente" /></SelectTrigger>
              <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Section 4: COMISION */}
      <div className={sectionCls}>
        <h2 className="font-outfit text-lg font-medium text-white border-b border-white/10 pb-3">4. Comision</h2>
        <p className="text-xs text-white/50">Valores por defecto: Venta = 3%, Arriendo = 1 canon (100%). Todos los porcentajes son editables.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Comision</Label>
            <Input type="number" value={form.commission_pct} onChange={e => upd('commission_pct', e.target.value)}
              placeholder={form.deal_type === 'venta' ? '3' : '100'} className={inputCls} />
          </div>
          {form.is_external_broker && (
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Aliado</Label>
              <Input type="number" value={form.external_split} onChange={e => upd('external_split', e.target.value)} className={inputCls} />
            </div>
          )}
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Agencia</Label>
            <Input type="number" value={form.agency_split} onChange={e => upd('agency_split', e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Broker Cliente</Label>
            <Input type="number" value={form.client_broker_split} onChange={e => upd('client_broker_split', e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Broker Propietario</Label>
            <Input type="number" value={form.owner_broker_split} onChange={e => upd('owner_broker_split', e.target.value)} className={inputCls} />
          </div>
          {form.deeplook_linked && (
            <div>
              <Label className="text-purple-400/80 text-xs uppercase tracking-wider mb-1.5 block">% Deeplook</Label>
              <Input type="number" value={form.deeplook_commission_pct} onChange={e => upd('deeplook_commission_pct', e.target.value)} className={inputCls} />
            </div>
          )}
        </div>
        <div className="p-4 bg-[#16302B] rounded-xl grid grid-cols-2 md:grid-cols-5 gap-4 border border-white/5">
          <div><p className="text-xs text-white/50 mb-1">Comision Total</p><p className="font-outfit text-lg font-bold text-[#A38560] tabular-nums">{formatCOP(commission.total)}</p></div>
          <div><p className="text-xs text-white/50 mb-1">Comision SELECTO</p><p className="font-outfit text-lg font-bold text-white tabular-nums">{formatCOP(commission.agency)}</p></div>
          <div><p className="text-xs text-white/50 mb-1">Broker Cliente</p><p className="font-outfit text-lg font-bold text-[#6BCB77] tabular-nums">{formatCOP(commission.clientBroker)}</p></div>
          <div><p className="text-xs text-white/50 mb-1">Broker Propietario</p><p className="font-outfit text-lg font-bold text-[#6BCB77] tabular-nums">{formatCOP(commission.ownerBroker)}</p></div>
          {form.deeplook_linked && <div><p className="text-xs text-purple-400/80 mb-1">Deeplook</p><p className="font-outfit text-lg font-bold text-purple-400 tabular-nums">{formatCOP(commission.deeplook)}</p></div>}
        </div>
      </div>

      <div className="flex gap-3 justify-end flex-col sm:flex-row">
        <button onClick={() => navigate('/negocios')} className="px-6 py-2.5 border border-white/20 text-white rounded-xl hover:bg-white/10 transition-all text-sm font-medium order-2 sm:order-1">
          Cancelar
        </button>
        <button onClick={save} disabled={saving} data-testid="save-deal-btn"
          className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-8 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50 order-1 sm:order-2">
          {saving ? 'Guardando...' : 'Guardar Negocio'}
        </button>
      </div>
    </div>
  );
}
